import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface ComboboxOption {
  value: string;
  label: string;
  /** Extra text shown under the label and included in the search match
   *  (e.g. an email under a name) - not shown once selected. */
  sublabel?: string;
}

// A searchable dropdown (shadcn's "Combobox" pattern: Popover + Command) -
// same (value, options, onChange) shape as SegmentedTabs, so call sites read
// the same way, but with a search box for lists too long to scan (country,
// employee pickers). Kept generic/reusable rather than duplicated per page.
export function Combobox({
  value,
  options,
  onChange,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyText = "No results found.",
  disabled,
  className,
  contentClassName
}: {
  value: string;
  options: ComboboxOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
  /** Overrides the popover's own width - defaults to matching the trigger
   *  button's width (`w-[--radix-popover-trigger-width]`), which is too
   *  narrow to read/search when the trigger itself is a short field (e.g. a
   *  3-letter currency code beside Amount/Category in a tight grid column).
   *  Pass a fixed width (e.g. "w-40") to decouple the popover from the
   *  trigger's own width in that case. */
  contentClassName?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const selected = options.find((o) => o.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal",
            !selected && "text-muted-foreground",
            className
          )}
        >
          <span className="truncate">
            {selected ? selected.label : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          contentClassName || "w-[--radix-popover-trigger-width]",
          "flex max-h-[--radix-popover-content-available-height] flex-col p-0"
        )}
        align="start"
      >
        <Command className="min-h-0">
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList className="max-h-none flex-1">
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={`${option.label} ${option.sublabel ?? ""}`}
                  onSelect={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === option.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <div className="min-w-0">
                    <p className="truncate">{option.label}</p>
                    {option.sublabel && (
                      <p className="truncate text-xs text-muted-foreground">
                        {option.sublabel}
                      </p>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
